import { persistedStorage } from '@platform/design-system';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Location } from './inventory.ts';

export type DepotUnits = { readonly locationId: string; readonly onHand: number };

export type LevelColumns = {
  readonly chosen: readonly string[] | null;
  readonly choose: (depotIds: readonly string[]) => void;
};

export const LEVEL_COLUMNS_KEY = 'admin.inventory.levelColumns';

const storedIds = (stored: unknown): readonly string[] | null =>
  Array.isArray(stored) && stored.every((id) => typeof id === 'string') ? stored : null;

export const useLevelColumns = create<LevelColumns>()(
  persist(
    (set) => ({
      chosen: null,
      choose: (depotIds) => {
        set({ chosen: depotIds });
      },
    }),
    {
      name: LEVEL_COLUMNS_KEY,
      storage: persistedStorage(),
      partialize: (state) => ({ chosen: state.chosen }),
      merge: (persisted, current) => ({
        ...current,
        chosen: storedIds((persisted as Partial<LevelColumns> | undefined)?.chosen),
      }),
    },
  ),
);

export const stockedDepotIdsOf = (
  locations: readonly Location[],
  units: readonly DepotUnits[],
): readonly string[] =>
  locations
    .filter((location) => units.some((depot) => depot.locationId === location.id && depot.onHand > 0))
    .map((location) => location.id);

export type ShownDepotsInput = {
  readonly locations: readonly Location[];
  readonly units: readonly DepotUnits[];
  readonly chosen: readonly string[] | null;
};

export const shownDepotIdsOf = ({ locations, units, chosen }: ShownDepotsInput): readonly string[] => {
  const known = locations.filter((location) => chosen?.includes(location.id)).map((location) => location.id);
  if (known.length > 0) return known;
  const stocked = stockedDepotIdsOf(locations, units);
  return stocked.length > 0 ? stocked : locations.map((location) => location.id);
};
